import { useEffect, useState } from "react";
import { api, gamePath, type Game } from "./api";
import type { Description, Observation } from "../protocol/ui";
import { buildTable } from "../model/table";
import { ZoneView } from "../components/ZoneView";
import { BoardView } from "../components/BoardView";
import { Avatar, Field } from "./GuildhallParts";
interface Frame {
    revision: number;
    historyLabel?: string;
    observation: Observation;
}
interface Review {
    revision: number;
    seat: string;
    description: Description;
    frames: Frame[];
}
export function PerspectiveReview({ game }: { game: Game }) {
    const [seat, setSeat] = useState(game.seats[0]?.id ?? "");
    const [revision, setRevision] = useState(0);
    const [latest, setLatest] = useState<number | null>(null);
    const [reply, setReply] = useState<Review | null>(null);
    const [error, setError] = useState("");
    const [inspected, setInspected] = useState<string | null>(null);
    useEffect(() => {
        let canceled = false;
        setError("");
        setReply(null);
        const from = Math.max(0, revision - 24);
        void api<Review>(
            `${gamePath(game.roomId, game.id)}/review?seat=${encodeURIComponent(seat)}&fromRevision=${from}&toRevision=${from + 49}`,
        )
            .then((value) => {
                if (!canceled) {
                    setReply(value);
                    setLatest(value.revision);
                }
            })
            .catch((err) => {
                if (!canceled)
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Review unavailable.",
                    );
            });
        return () => {
            canceled = true;
        };
    }, [game.id, seat, revision]);
    const frame =
        reply?.frames.find((f) => f.revision === revision) ?? reply?.frames[0];
    const table =
        frame && reply
            ? buildTable(frame.observation, reply.description, seat)
            : null;
    const controller = (game.controllerHistory ?? [])
        .slice()
        .reverse()
        .find(
            (c) =>
                c.seat === seat &&
                c.fromRevision <= revision &&
                (c.toRevision === null || revision <= c.toRevision),
        );
    const seatIndex = new Map(game.seats.map((s, i) => [s.id, i]));
    return (
        <section
            className="guild-review"
            aria-label="Read-only completed game review"
        >
            <h3>Sit in someone’s chair</h3>
            <p className="guild-hint">
                You see only what this player could see at that point. Review
                cannot change the game.
            </p>
            <div className="guild-perspective-seats">
                {game.seats.map((s) => (
                    <button
                        type="button"
                        aria-pressed={seat === s.id}
                        key={s.id}
                        onClick={() => {
                            setSeat(s.id);
                            setInspected(null);
                        }}
                    >
                        <Avatar
                            profile={s.profile}
                            name={s.name ?? s.id}
                            bot={s.bot}
                        />
                        {s.profile?.displayName ?? s.name ?? s.id} · Seat{" "}
                        {(seatIndex.get(s.id) ?? 0) + 1}
                    </button>
                ))}
            </div>
            <Field
                label={`Revision ${revision}${latest !== null ? ` of ${latest}` : ""}`}
            >
                <input
                    aria-label="Review revision"
                    type="range"
                    min={0}
                    max={latest ?? revision}
                    value={revision}
                    onChange={(e) => setRevision(Number(e.target.value))}
                />
            </Field>
            {controller && (
                <p className="guild-hint">
                    Recorded controller:{" "}
                    {controller.profile?.displayName ??
                        controller.name ??
                        (controller.bot ? "Bot" : "Player")}{" "}
                    · Seat {(seatIndex.get(seat) ?? 0) + 1}. Controller changes
                    at this revision apply to subsequent decisions.
                </p>
            )}
            <div className="guild-actions">
                <button
                    type="button"
                    disabled={revision === 0}
                    onClick={() => setRevision((r) => Math.max(0, r - 1))}
                >
                    Previous
                </button>
                <button
                    type="button"
                    disabled={latest === null || revision >= latest}
                    onClick={() => setRevision((r) => r + 1)}
                >
                    Next
                </button>
                <button
                    type="button"
                    disabled={latest === null}
                    onClick={() => {
                        if (latest !== null) setRevision(latest);
                    }}
                >
                    Final position
                </button>
            </div>
            {error && <p role="alert">{error}</p>}
            {table ? (
                <div className="guild-review-table">
                    <p>{table.status}</p>
                    <div className="guild-review-counters">
                        {table.counters.map((c) => (
                            <span key={c.id}>
                                {c.label}: {String(c.value)}
                            </span>
                        ))}
                    </div>
                    {table.zones.map((zone) =>
                        zone.placement === "board" ? (
                            <BoardView
                                key={zone.id}
                                zone={zone}
                                table={table}
                                selectable={new Set()}
                                inspected={inspected}
                                onInspect={setInspected}
                                seatIndex={seatIndex}
                            />
                        ) : (
                            <ZoneView
                                key={zone.id}
                                zone={zone}
                                selectable={new Set()}
                                inspected={inspected}
                                onInspect={setInspected}
                                seatIndex={seatIndex}
                                attached={new Map()}
                            />
                        ),
                    )}
                    {inspected && table.objects.get(inspected) && (
                        <aside className="guild-panel">
                            <h4>{table.objects.get(inspected)!.title}</h4>
                            <p>{table.objects.get(inspected)!.subtitle}</p>
                            {table.objects.get(inspected)!.details.map((d) => (
                                <p key={d.key}>
                                    {d.key}: {d.value}
                                </p>
                            ))}
                        </aside>
                    )}
                </div>
            ) : (
                !error && <p role="status">Opening the retained perspective…</p>
            )}
        </section>
    );
}
